import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, CircularProgress } from "@mui/material";
import { boldRuns, formatReply } from "./assistantProtocol";

const Inline = ({ text }) =>
  boldRuns(text).map((run, index) =>
    run.bold ? (
      <strong key={index} className="font-semibold text-slate-900">
        {run.text}
      </strong>
    ) : (
      <span key={index}>{run.text}</span>
    )
  );

const ReplyText = ({ text }) => (
  <div className="space-y-2 text-sm leading-relaxed text-slate-700">
    {formatReply(text).map((block, index) =>
      block.type === "list" ? (
        <ul key={index} className="m-0 pl-5 space-y-1 list-disc">
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>
              <Inline text={item} />
            </li>
          ))}
        </ul>
      ) : (
        <p key={index} className="m-0 whitespace-pre-wrap">
          <Inline text={block.text} />
        </p>
      )
    )}
  </div>
);

const Step = ({ step }) => (
  <li className="flex items-center gap-2 text-xs text-slate-500">
    {step.state === "running" ? (
      <CircularProgress size={12} thickness={5} />
    ) : step.state === "failed" ? (
      <ErrorOutlineIcon sx={{ fontSize: 14 }} className="text-rose-500" />
    ) : (
      <CheckCircleOutlineIcon sx={{ fontSize: 14 }} className="text-emerald-500" />
    )}
    <span className={step.state === "running" ? "text-slate-700" : ""}>{step.label}</span>
  </li>
);

const PROPOSAL_STATUS = {
  approved: { label: "Done", className: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "Cancelled", className: "bg-slate-100 text-slate-600" },
};

/** A change the agent wants to make. Nothing is saved until the user approves it here. */
const ProposalCard = ({ proposal, onDecide }) => {
  const settled = PROPOSAL_STATUS[proposal.status];
  const working = proposal.status === "working";
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
            Needs your approval
          </p>
          <p className="m-0 mt-0.5 text-sm font-semibold text-slate-900">{proposal.title}</p>
        </div>
        {settled ? (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${settled.className}`}
          >
            {settled.label}
          </span>
        ) : null}
      </div>
      {proposal.summary ? (
        <p className="m-0 mt-1 text-xs text-slate-600">{proposal.summary}</p>
      ) : null}
      {proposal.fields?.length ? (
        <dl className="m-0 mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-white px-3 py-2 text-xs">
          {proposal.fields.map((field) => (
            <div key={field.label} className="contents">
              <dt className="text-slate-500">{field.label}</dt>
              <dd className="m-0 text-slate-800 break-words">{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {proposal.note ? <p className="m-0 mt-2 text-xs text-slate-600">{proposal.note}</p> : null}
      {settled ? null : (
        <div className="mt-3 flex justify-end gap-2">
          <Button size="small" disabled={working} onClick={() => onDecide(proposal.id, "reject")}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={working}
            onClick={() => onDecide(proposal.id, "approve")}
          >
            {working ? "Working…" : "Approve"}
          </Button>
        </div>
      )}
    </div>
  );
};

const TypingDots = () => (
  <span className="inline-flex items-center gap-1 py-1" aria-label="Assistant is thinking">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </span>
);

const AssistantMessage = ({ message, onDecide }) => {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <p className="m-0 max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-teal-700 px-3.5 py-2 text-sm text-white">
          {message.text}
        </p>
      </div>
    );
  }

  const empty = !message.text && !message.steps.length && !message.proposals.length;
  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-700">
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        {message.steps.length ? (
          <ul className="m-0 list-none space-y-1 p-0">
            {message.steps.map((step) => (
              <Step key={step.id} step={step} />
            ))}
          </ul>
        ) : null}
        {message.text ? <ReplyText text={message.text} /> : null}
        {message.streaming && empty ? <TypingDots /> : null}
        {message.proposals.map((proposal) => (
          <ProposalCard key={proposal.id} proposal={proposal} onDecide={onDecide} />
        ))}
        {message.error ? (
          <p className="m-0 flex items-center gap-1.5 text-xs text-rose-600">
            <ErrorOutlineIcon sx={{ fontSize: 14 }} />
            {message.error}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default AssistantMessage;
